import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Building2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtCompact } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageContainer, PageHeader, EmptyState, Skeleton, UserAvatar, Segmented, SearchField, CompanyLogo, InlineError } from "@/components/common";

const INDUSTRIES = ["Media & Entertainment", "Broadcast", "Corporate", "Public", "Education", "Energy", "Finance", "Other"];
const COUNTRIES = ["Norway", "Sweden", "Denmark", "Finland", "UAE", "Other"];

export function NewCustomerDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", industry: "Corporate", country: "Norway", city: "", website: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", industry: "Corporate", country: "Norway", city: "", website: "" }); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/accounts", { ...form, currency: form.country === "Sweden" ? "SEK" : form.country === "Denmark" ? "DKK" : "NOK", legal_entity: form.country === "Sweden" ? "Mediability Sweden AB" : form.country === "Denmark" ? "Mediability Denmark ApS" : "Mediability Norway AS" });
      toast.success(`${r.data.name} created`);
      onOpenChange(false);
      onCreated?.(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-customer-dialog">
        <DialogHeader><DialogTitle>New customer</DialogTitle><DialogDescription>Only the essentials. Everything else fills in as you work.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="c-name">Company name</Label><Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 rounded-[10px]" autoFocus required data-testid="new-customer-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Industry</Label><Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}><SelectTrigger className="h-10 rounded-[10px]" data-testid="new-customer-industry-select"><SelectValue /></SelectTrigger><SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Country</Label><Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}><SelectTrigger className="h-10 rounded-[10px]" data-testid="new-customer-country-select"><SelectValue /></SelectTrigger><SelectContent>{COUNTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label htmlFor="c-city">City</Label><Input id="c-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-10 rounded-[10px]" data-testid="new-customer-city-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-web">Website</Label><Input id="c-web" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="example.com" className="h-10 rounded-[10px]" data-testid="new-customer-website-input" /></div>
          </div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button type="submit" disabled={busy || !form.name.trim()} data-testid="new-customer-submit-button">{busy ? "Creating..." : "Create customer"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomersPage() {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("all");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/accounts").then((r) => setAccounts(r.data)).catch((e) => setError(errMsg(e)));
  useEffect(() => { load(); }, []);

  const industries = useMemo(() => Array.from(new Set((accounts || []).map((a) => a.industry).filter(Boolean))).sort(), [accounts]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (accounts || []).filter((a) => (industry === "all" || a.industry === industry) && (!s || `${a.name} ${a.city} ${a.industry} ${a.owner_name}`.toLowerCase().includes(s))).sort((a, b) => (b.open_value || 0) - (a.open_value || 0) || a.name.localeCompare(b.name));
  }, [accounts, q, industry]);

  const totals = useMemo(() => {
    const open = (accounts || []).reduce((s, a) => s + (a.open_value || 0), 0);
    const won = (accounts || []).reduce((s, a) => s + (a.won_revenue || 0), 0);
    return { open, won };
  }, [accounts]);
  const industryItems = useMemo(() => [{ key: "all", label: "All", count: accounts?.length }, ...industries.map((i) => ({ key: i, label: i, count: (accounts || []).filter((a) => a.industry === i).length }))], [industries, accounts]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Workspace"
        title="Customers"
        description={accounts ? (
          <span className="inline-flex flex-wrap items-center gap-x-2">
            <span><span className="font-semibold text-foreground tabular">{accounts.length}</span> customers</span>
            <span className="text-border">·</span>
            <span><span className="font-semibold text-foreground tabular">{fmtCompact(totals.open)}</span> open pipeline</span>
            <span className="text-border">·</span>
            <span><span className="font-semibold text-foreground tabular">{fmtCompact(totals.won)}</span> won</span>
          </span>
        ) : "Accounts, contacts and everything that has happened with them."}
        actions={<Button className="gap-2" onClick={() => setOpen(true)} data-testid="customers-new-button"><Plus className="h-4 w-4" /> New customer</Button>}
      />
      {error && <InlineError onRetry={load} testId="customers-error">{error}</InlineError>}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Segmented items={industryItems} value={industry} onChange={setIndustry} testIdPrefix="customers-industry" />
        <SearchField value={q} onChange={setQ} placeholder="Search customers, cities, owners" className="w-full lg:w-[300px]" autoFocus testId="customers-search-input" />
      </div>
      {!accounts ? (
        <div className="surface-flat divide-y divide-black/[0.05]">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="flex gap-4 px-5 py-4"><Skeleton className="h-10 w-10 rounded-xl" /><div className="flex-1"><Skeleton className="h-4 w-1/3" /><Skeleton className="mt-2 h-3 w-1/4" /></div></div>)}</div>
      ) : list.length === 0 ? (
        <EmptyState icon={Building2} title={q ? `No customers match "${q}"` : "No customers yet"} description="Customers are also created automatically from inquiries, discovery forms and tenders." action={<Button onClick={() => setOpen(true)}>New customer</Button>} testId="customers-empty" />
      ) : (
        <div className="surface-flat overflow-hidden" data-testid="customers-list">
          <div className="hidden grid-cols-[1fr_130px_130px_90px_120px_32px] items-center gap-4 border-b border-black/[0.06] bg-[hsl(40_12%_98%)] px-5 py-2 md:grid">
            <span className="eyebrow text-[10px]">Customer</span>
            <span className="eyebrow text-right text-[10px]">Open pipeline</span>
            <span className="eyebrow text-right text-[10px]">Won revenue</span>
            <span className="eyebrow text-right text-[10px]">Contacts</span>
            <span className="eyebrow text-[10px]">Owner</span>
            <span />
          </div>
          <div className="divide-y divide-black/[0.05]">
            {list.map((a) => (
              <button key={a.id} type="button" onClick={() => navigate(`/customers/${a.id}`)} className="group grid w-full grid-cols-[1fr_auto] items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-[hsl(40_12%_98%)] focus-visible:bg-[hsl(40_12%_98%)] focus-visible:outline-none md:grid-cols-[1fr_130px_130px_90px_120px_32px]" data-testid="customers-row">
                <div className="flex min-w-0 items-center gap-3.5">
                  <CompanyLogo name={a.name} domain={a.website} />
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-semibold tracking-[-0.01em]" data-testid="customers-row-name">{a.name}</div>
                    <div className="truncate text-[12.5px] text-muted-foreground">{[a.industry, a.city, a.country].filter(Boolean).join(" · ")}</div>
                  </div>
                </div>
                <div className="text-right md:hidden"><div className="text-[13.5px] font-semibold tabular">{a.open_value ? fmtCompact(a.open_value) : "–"}</div><div className="text-[11.5px] text-muted-foreground">{a.open_count} open</div></div>
                <div className="hidden text-right md:block"><div className="text-[13.5px] font-semibold tabular">{a.open_value ? fmtCompact(a.open_value) : "–"}</div><div className="text-[11.5px] text-muted-foreground">{a.open_count} open</div></div>
                <div className="hidden text-right md:block"><div className="text-[13.5px] tabular">{a.won_revenue ? fmtCompact(a.won_revenue) : "–"}</div><div className="text-[11.5px] text-muted-foreground">{a.won_count ? `${a.won_count} won` : "no orders"}</div></div>
                <div className="hidden text-right text-[13px] tabular text-muted-foreground md:block">{a.contacts_count}</div>
                <div className="hidden items-center gap-2 text-[12.5px] text-muted-foreground md:flex"><UserAvatar name={a.owner_name} initials={a.owner_initials} color={a.owner_color || "#111"} /> <span className="truncate">{(a.owner_name || "").split(" ")[0]}</span></div>
                <ArrowUpRight className="hidden h-4 w-4 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-primary md:block" />
              </button>
            ))}
          </div>
        </div>
      )}
      <NewCustomerDialog open={open} onOpenChange={setOpen} onCreated={(a) => { load(); navigate(`/customers/${a.id}`); }} />
    </PageContainer>
  );
}
